"use client";

import { formatTabTitle, getRouteTabTitle } from "@/lib/tab-title";
import { usePathname } from "next/navigation";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

type TabTitleOverride = {
  pathname: string;
  status?: string | null;
  subject: string;
  token: symbol;
};

type TabTitleContextValue = {
  clearOverride: (token: symbol) => void;
  setOverride: (override: TabTitleOverride) => void;
};

const TabTitleContext = createContext<TabTitleContextValue | null>(null);

export function TabTitleProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const [override, setOverrideState] = useState<TabTitleOverride | null>(null);

  const contextValue = useMemo<TabTitleContextValue>(
    () => ({
      clearOverride: (token) => {
        setOverrideState((current) => (current?.token === token ? null : current));
      },
      setOverride: setOverrideState,
    }),
    []
  );

  const activeOverride = override?.pathname === pathname ? override : null;
  const documentTitle = formatTabTitle(
    activeOverride?.subject ?? getRouteTabTitle(pathname),
    activeOverride?.status
  );

  return (
    <TabTitleContext.Provider value={contextValue}>
      <title>{documentTitle}</title>
      {children}
    </TabTitleContext.Provider>
  );
}

export function useTabTitle(subject?: string | null, status?: string | null) {
  const context = useContext(TabTitleContext);
  const pathname = usePathname();
  const tokenRef = useRef<symbol>(Symbol("tab-title"));

  useEffect(() => {
    if (!context || !subject?.trim()) {
      return;
    }

    const token = tokenRef.current;
    context.setOverride({ pathname, status, subject, token });
    return () => context.clearOverride(token);
  }, [context, pathname, status, subject]);
}

export function TabTitle({
  status,
  subject,
}: {
  status?: string | null;
  subject?: string | null;
}) {
  useTabTitle(subject, status);
  return null;
}
